import { useTu } from "@src/appV2/i18n/useTu";

/* eslint-disable import/max-dependencies, max-lines */
import { isDefined } from "@clipboard-health/util-ts";

import { zodResolver } from "@hookform/resolvers/zod";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
} from "@mui/material";

import { logEvent } from "@src/appV2/lib/analytics";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";

import {
  ChartingCredentialSoftware,
  type ChartingCredentialSource,
  type CredentialsFormData,
  credentialsFormSchema,
  WORKER_CREDENTIAL_TYPE,
} from "@src/appV2/WorkerCredentials/api/schema";

import { useCreateWorkerCredentials } from "@src/appV2/WorkerCredentials/api/useCreateWorkerCredentials";
import { useSetupShareCredentialsForm } from "@src/appV2/WorkerCredentials/api/useSetupShareCredentialsForm";
import { VendorTimeclockCredentials } from "@src/appV2/WorkerCredentials/VendorTimeclockCredentials/VendorTimeclockCredentials";
import { type UseModalState } from "@src/lib/ui-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { environmentConfig } from "../../../environment";

import {
  getWorkerExternalIntegrationConfigurationUrl,
  useGetWorkerExternalIntegrationConfiguration,
} from "../../../ExternalIntegrations/api/useGetWorkerExternalIntegrationConfiguration";

import { useUpdateWorkerExternalIntegrationConfiguration } from "../../../ExternalIntegrations/api/useUpdateWorkerExternalIntegrationConfiguration";
import { useUpdateDefaultChartingPassword } from "../../../FacilityProfile/api/useUpdateDefaultChartingPassword";
import { APP_V2_USER_EVENTS, useToast } from "../../../lib";

interface ShareCredentialsDialogProps {
  modalState: UseModalState;
  workerId: string;
  workplaceId: string;
  onSuccess: () => void;
  source: ChartingCredentialSource;
  workerName: string | undefined;
}

export function ShareCredentialsDialog(props: ShareCredentialsDialogProps) {
  const { tu } = useTu("myProfessionals");

  const { modalState, workerId, workplaceId, onSuccess, source, workerName } = props;
  const isMobile = useIsMobile();
  const { mutateAsync: createWorkerCredentials } = useCreateWorkerCredentials();
  const { mutateAsync: updateDefaultChartingPassword } = useUpdateDefaultChartingPassword();

  const { mutateAsync: updateWorkerExternalIntegrationConfiguration } =
    useUpdateWorkerExternalIntegrationConfiguration();

  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();

  const {
    isLoading,
    refetchChartingCredentials,
    timeclockVendor,
    showCharting,
    chartingCredentials,
    timeclockCredentials,
    defaultChartingSoftware,
    setDefaultChartingSoftware,
    initialChartingPassword,
    shouldUpdateDefaultChartingPassword,
    setShouldUpdateDefaultChartingPassword,
    showPassword,
    setShowPassword,
    showPin,
    setShowPin,
    missingInitialCredentials,
  } = useSetupShareCredentialsForm({ workplaceId, workerId });

  const {
    data: existingConfiguration,
    isError: isExternalIdError,
    error: externalIdError,
    isLoading: isLoadingConfiguration,
  } = useGetWorkerExternalIntegrationConfiguration({
    workerId,
    workplaceId,
  });

  const is404Error = externalIdError?.response?.status === 404;

  const externalIdErrorPlaceholder = is404Error
    ? tu("Professional not onboarded yet")
    : tu("An error occurred loading person number");

  const initialFormValues = useMemo<CredentialsFormData>(
    () => ({
      chartingSoftware:
        chartingCredentials?.software ??
        defaultChartingSoftware ??
        ChartingCredentialSoftware.POINT_CLICK_CARE,
      username: chartingCredentials?.username ?? "",
      password: initialChartingPassword,
      timeclockPin: timeclockCredentials?.pin ?? "",
      timeclockPassword: timeclockCredentials?.password ?? "",
      timeclockBadge: timeclockCredentials?.badgeNumber ?? "",
      externalId: existingConfiguration?.attributes.externalId ?? undefined,
    }),
    [
      chartingCredentials,
      defaultChartingSoftware,
      existingConfiguration,
      initialChartingPassword,
      timeclockCredentials,
    ]
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsFormSchema),
    values: initialFormValues,
  });

  const selectedSoftware = watch("chartingSoftware");
  const title = missingInitialCredentials ? tu("Share credentials") : tu("Edit credentials");
  const isDialogLoading = isLoadingConfiguration || isLoading;

  async function onSubmit({
    username,
    password,
    timeclockPin,
    timeclockPassword,
    timeclockBadge,
    externalId,
  }: CredentialsFormData) {
    try {
      setDefaultChartingSoftware(selectedSoftware);
      try {
        await createWorkerCredentials({
          data: {
            type: WORKER_CREDENTIAL_TYPE,
            attributes: {
              workerId,
              workplaceId,
              chartingCredentials: {
                software: selectedSoftware,
                username,
                password,
                source,
              },
              timeclockCredentials: {
                pin: timeclockPin ?? "",
                badgeNumber: timeclockBadge ?? "",
                ...(timeclockPassword ? { password: timeclockPassword } : {}),
              },
            },
          },
        });
      } catch {
        showErrorToast(tu("Failed to update worker credentials."));
        return;
      }

      if (isDefined(existingConfiguration) && isDefined(externalId)) {
        try {
          await updateWorkerExternalIntegrationConfiguration({
            ...existingConfiguration.attributes,
            externalId,
          });
          logEvent(APP_V2_USER_EVENTS.EXTERNAL_ID_UPDATED, {
            workerId,
            workplaceId,
          });
        } catch {
          showErrorToast(
            tu("Credentials were updated, but the external ID could not be saved. Please retry.")
          );
          return;
        }
      }

      if (shouldUpdateDefaultChartingPassword) {
        try {
          await updateDefaultChartingPassword({
            workplaceId,
            defaultChartingPassword: password,
          });
        } catch {
          showErrorToast(
            tu(
              "Credentials were updated, but the workplace default password could not be saved. Please retry."
            )
          );
          return;
        }
      }

      showSuccessToast(tu("Credentials updated successfully"));
      onSuccess();
      modalState.closeModal();
      await queryClient.invalidateQueries({
        queryKey: [
          `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/worker-credentials`,
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: [getWorkerExternalIntegrationConfigurationUrl()],
      });
      await refetchChartingCredentials();
    } catch {
      showErrorToast(tu("Failed to share credentials, please try again."));
    }
  }

  return (
    <BerlinThemeProvider>
      <ResponsiveDialog
        modalState={modalState}
        title={title}
        width={560}
        footerContent={
          <DialogFooter
            layout={isMobile ? "vertical" : "horizontal"}
            primaryAction={{
              label: missingInitialCredentials ? tu("Share credentials") : tu("Save credentials"),
              onClick: handleSubmit(onSubmit),
              isLoading: isSubmitting,
              disabled: isDialogLoading || isSubmitting,
            }}
            secondaryAction={{
              label: tu("Cancel"),
              onClick: modalState.closeModal,
            }}
          />
        }
      >
        {isDialogLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Stack spacing={4}>
            {workerName && (
              <Stack spacing={1}>
                <Text semibold variant="body2">
                  {workerName}
                </Text>
                <Text sx={(theme) => ({ color: theme.palette.base?.muted })} variant="body2">
                  {tu(
                    "Keep this aligned with the workplace system so the professional can start cleanly."
                  )}
                </Text>
              </Stack>
            )}

            {showCharting && (
              <Stack spacing={3}>
                <Controller
                  control={control}
                  name="chartingSoftware"
                  render={({ field }) => (
                    <TextInput fullWidth select label={tu("Charting software")} {...field}>
                      {Object.values(ChartingCredentialSoftware).map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </TextInput>
                  )}
                />
                <TextInput
                  fullWidth
                  label={tu("Charting username")}
                  error={Boolean(errors.username)}
                  helperText={errors.username?.message}
                  {...register("username")}
                />
                <TextInput
                  fullWidth
                  label={tu("Charting password")}
                  type={showPassword ? "text" : "password"}
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={tu("Toggle password visibility")}
                          edge="end"
                          onClick={() => {
                            setShowPassword(!showPassword);
                          }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  {...register("password")}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shouldUpdateDefaultChartingPassword}
                      onChange={(_, checked) => {
                        setShouldUpdateDefaultChartingPassword(checked);
                      }}
                    />
                  }
                  label={tu("Save as workplace default password")}
                />
              </Stack>
            )}

            <VendorTimeclockCredentials
              timeclockVendor={timeclockVendor}
              register={register}
              errors={errors}
              showPin={showPin}
              externalIdErrorPlaceholder={
                isExternalIdError ? externalIdErrorPlaceholder : undefined
              }
              onTogglePinVisibility={() => {
                setShowPin(!showPin);
              }}
            />
          </Stack>
        )}
      </ResponsiveDialog>
    </BerlinThemeProvider>
  );
}
/* eslint-enable import/max-dependencies, max-lines */
